// src/components/modules/utilizationApi.js
// API calls + date-range helpers for the Project Utilization page.

import { Timesheetapi } from "../../middleware/interceptor";
import { toISO } from "./dashboardStats";

const ENDPOINTS = {
  activeClients: "api/Utilization/GetActiveClients",
  clientUtilization: "api/Utilization/GetClientUtilization",
  activeProjects: "api/Utilization/GetActiveProjects",
  projectUtilization: "api/Utilization/GetProjectUtilization",
  employeeUtilization: "api/Utilization/GetEmployeeUtilization",   // EmployeeUtilizationController.cs
  fpsUtilization: "api/Utilization/GetFpsUtilization",             // UtilizationController.cs: FPS projects, all clients
};

// FPS project = "FPS" as a separate word in the project name ("Accuris - 45552067 | FPS").
// Same rule as IsFps() in UtilizationController.cs.
export const isFps = (name) => /(^|[^a-z0-9])fps([^a-z0-9]|$)/i.test(String(name || ""));

const who = () => ({
  empid: localStorage.getItem("employee_id") || "",
  roleId: localStorage.getItem("emp_role_id") || "",
});

const unwrap = (res, key) => {
  if (res?.data?.status !== 1) throw new Error(res?.data?.message || "Request failed");
  return key ? res.data[key] : res.data;
};

export async function fetchActiveProjects(range) {
  const res = await Timesheetapi.get(ENDPOINTS.activeProjects, {
    params: { ...who(), fromDate: toISO(range.from), toDate: toISO(range.to) },
  });
  return unwrap(res, "projects") || [];
}

// group: "all" | "freshworks" (logs time in Rocketlane) | "legacy" (everyone else)
export async function fetchActiveClients(range, group = "all") {
  const res = await Timesheetapi.get(ENDPOINTS.activeClients, {
    params: { ...who(), fromDate: toISO(range.from), toDate: toISO(range.to), group },
  });
  return unwrap(res, "clients") || [];
}

// Admin view → Employees: { group_counts, employees: [{ ..., clients: [{ ..., projects: [...] }] }] }
export async function fetchEmployeeUtilization(range, group = "all") {
  const res = await Timesheetapi.get(ENDPOINTS.employeeUtilization, {
    params: { ...who(), fromDate: toISO(range.from), toDate: toISO(range.to), group },
  });
  return unwrap(res);
}

// Both return { scope, range, projects, employees } — same shape, so one detail view renders either
export async function fetchClientUtilization(clientId, range) {
  const res = await Timesheetapi.get(ENDPOINTS.clientUtilization, {
    params: { ...who(), clientId, fromDate: toISO(range.from), toDate: toISO(range.to) },
  });
  return unwrap(res);
}

// Every FPS project (all clients) and who worked on them — same shape as fetchClientUtilization
export async function fetchFpsUtilization(range) {
  const res = await Timesheetapi.get(ENDPOINTS.fpsUtilization, {
    params: { ...who(), fromDate: toISO(range.from), toDate: toISO(range.to) },
  });
  return unwrap(res);
}

export async function fetchProjectUtilization(projectId, range) {
  const res = await Timesheetapi.get(ENDPOINTS.projectUtilization, {
    params: { ...who(), projectId, fromDate: toISO(range.from), toDate: toISO(range.to) },
  });
  return unwrap(res);
}

// ── Period helpers ─────────────────────────────────────────────────────
// mode: "day" | "week" | "month" | "year"; anchor: any Date inside the period.

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function periodRange(mode, anchor) {
  const a = startOfDay(anchor);
  switch (mode) {
    case "day":
      return { from: a, to: a };
    case "week": {
      const from = new Date(a);
      from.setDate(a.getDate() - ((a.getDay() + 6) % 7)); // Monday
      const to = new Date(from);
      to.setDate(from.getDate() + 6);
      return { from, to };
    }
    case "year":
      return { from: new Date(a.getFullYear(), 0, 1), to: new Date(a.getFullYear(), 11, 31) };
    case "month":
    default:
      return { from: new Date(a.getFullYear(), a.getMonth(), 1), to: new Date(a.getFullYear(), a.getMonth() + 1, 0) };
  }
}

export function shiftAnchor(mode, anchor, step) {
  const d = startOfDay(anchor);
  if (mode === "day") d.setDate(d.getDate() + step);
  else if (mode === "week") d.setDate(d.getDate() + 7 * step);
  else if (mode === "year") d.setFullYear(d.getFullYear() + step);
  else d.setMonth(d.getMonth() + step, 1);
  return d;
}

export function periodLabel(mode, range) {
  const { from, to } = range;
  if (mode === "day") return from.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  if (mode === "week") {
    const f = from.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const t = to.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    return `${f} – ${t}`;
  }
  if (mode === "year") return String(from.getFullYear());
  return from.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// Buckets for the chart: year → months, month/week → days, day → none
export function buildBuckets(mode, range) {
  const out = [];
  if (mode === "year") {
    for (let m = 0; m < 12; m++) {
      const d = new Date(range.from.getFullYear(), m, 1);
      out.push({
        key: `${d.getFullYear()}-${String(m + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString("en-GB", { month: "short" }),
        full: d.toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
      });
    }
  } else if (mode === "month" || mode === "week") {
    for (let d = new Date(range.from); d <= range.to; d.setDate(d.getDate() + 1)) {
      out.push({
        key: toISO(d),
        label: mode === "week" ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" }) : String(d.getDate()),
        full: d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
        weekend: d.getDay() === 0 || d.getDay() === 6,
      });
    }
  }
  return out;
}

export const bucketKeyFor = (mode, isoDate) => (mode === "year" ? isoDate.slice(0, 7) : isoDate);